// React and related libraries
import React, { useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

// Third-party libraries
import Avatar from "@mui/joy/Avatar";
import Box from "@mui/joy/Box";
import BusinessCenter from "@mui/icons-material/BusinessCenter";
import Button from "devextreme-react/button";
import { confirm } from "devextreme/ui/dialog";
import Divider from "@mui/joy/Divider";
import GlobalStyles from "@mui/joy/GlobalStyles";
import List from "@mui/joy/List";
import ListItem from "@mui/joy/ListItem";
import ListItemButton, { listItemButtonClasses } from "@mui/joy/ListItemButton";
import ListItemContent from "@mui/joy/ListItemContent";
import Sheet from "@mui/joy/Sheet";
import Typography from "@mui/joy/Typography";
import IconButton from "@mui/joy/IconButton";
import CampaignIcon from "@mui/icons-material/Campaign";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import AddchartRoundedIcon from "@mui/icons-material/AddchartRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import QueryStatsRoundedIcon from "@mui/icons-material/QueryStatsRounded";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import AccountCircleRoundedIcon from "@mui/icons-material/AccountCircleRounded";

// Local components, hooks, features, and config
import { logout } from "../features/authSlice";
import { admin, vendor, notice, management } from "../menuItems";
import { closeSidebar, toggleSidebar } from "../utils";
import packageJson from "../../package.json";

// 메뉴 열고 접기
function Toggler({ defaultExpanded = true, renderToggle, children }) {
    const [open, setOpen] = useState(defaultExpanded);

    return (
        <>
            {renderToggle({ open, setOpen })}
            <Box
                sx={{
                    display: "grid",
                    gridTemplateRows: open ? "1fr" : "0fr",
                    transition: "0.2s ease",
                    "& > *": {
                        overflow: "hidden",
                    },
                }}
            >
                {children}
            </Box>
        </>
    );
}

function Sidebar() {
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const userInfo = useSelector((state) => state.auth.userInfo);

    const { title, color } = useMemo(() => {
        if (process.env.REACT_APP_API_URL.includes("apidev.dsnl.co.kr"))
            return { title: "QMS DSNL (DEV)", color: "#D32F2F" }; // 개발: 빨간색 계열
        else if (process.env.REACT_APP_API_URL.includes("api.dsnl.co.kr"))
            return { title: "QMS DSNL", color: "#00137F" }; // 운영: 기존 파란색
        else return { title: "QMS DSNL (TEST)", color: "#ED6C02" }; // 테스트: 주황색 계열
    }, []);

    const navigateHome = useCallback(() => {
        // On mobile, close sidebar after menu click
        if (document.body.style.getPropertyValue("--SideNavigation-slideIn") === "1") {
            toggleSidebar();
        }

        navigate("/");
    }, [navigate]);

    function handleLogout() {
        var result = confirm("로그아웃 하시겠습니까?", "Confirm");
        result.done(function (dialogResult) {
            if (dialogResult) {
                dispatch(logout());
                window.location.href = "/login"; // 브라우저 강제 이동으로 JS 메모리 및 전역 데이터 소스 캐시 초기화
            }
        });
    }

    // 메뉴 화면 열기
    const handleMenuClick = (url) => {
        navigate(url, { replace: true, state: { from: location.pathname } });
        // On mobile, close sidebar after menu click
        if (document.body.style.getPropertyValue("--SideNavigation-slideIn") === "1") {
            toggleSidebar();
        }
    };

    return (
        <Sheet
            className="Sidebar"
            sx={{
                fontWeight: 500,
                position: { xs: "fixed", md: "sticky" },
                transform: {
                    xs: "translateX(calc(100% * (var(--SideNavigation-slideIn, 0) - 1)))",
                    md: "none",
                },
                transition: "transform 0.4s, width 0.4s",
                zIndex: 100,
                height: "100dvh",
                width: "var(--Sidebar-width)",
                top: 0,
                p: 2,
                flexShrink: 0,
                display: "flex",
                flexDirection: "column",
                gap: 2,
                borderRight: "1px solid",
                borderColor: "divider",
            }}
        >
            <GlobalStyles
                styles={(theme) => ({
                    ":root": {
                        "--Sidebar-width": "220px",
                        [theme.breakpoints.up("lg")]: {
                            "--Sidebar-width": "240px",
                        },
                    },
                })}
            />
            <Box
                className="Sidebar-overlay"
                sx={{
                    position: "fixed",
                    zIndex: 9998,
                    top: 0,
                    left: 0,
                    width: "100vw",
                    height: "100vh",
                    opacity: "var(--SideNavigation-slideIn)",
                    backgroundColor: "var(--joy-palette-background-backdrop)",
                    transition: "opacity 0.4s",
                    transform: {
                        xs: "translateX(calc(100% * (var(--SideNavigation-slideIn, 0) - 1) + var(--SideNavigation-slideIn, 0) * var(--Sidebar-width, 0px)))",
                        lg: "translateX(-100%)",
                    },
                }}
                onClick={() => closeSidebar()}
            />
            <Box sx={{ display: "flex", gap: 0, alignItems: "center", marginBottom: 0 }}>
                <IconButton variant="soft" color="primary" size="sm">
                    <QueryStatsRoundedIcon />
                </IconButton>
                <Typography level="title-lg" sx={{ fontWeight: 600, color: color, fontFamily: "Playfair Display" }}>
                    {title}
                </Typography>
            </Box>
            <Button icon="greaterorequal" variant="plain" text="모든탭 닫기" stylingMode="text" type="normal" textAlign="left" onClick={navigateHome} style={{ width: "130px", marginLeft: 0, marginRight: 0, marginBottom: 0 }} />
            <Box
                aria-label="list of files"
                marginLeft={1}
                sx={{
                    minHeight: 0,
                    overflow: "hidden auto",
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                    [`& .${listItemButtonClasses.root}`]: {
                        gap: 1.5,
                    },
                }}
            >
                <List
                    size="sm"
                    sx={{
                        gap: 1,
                        "--List-nestedInsetStart": "30px",
                        "--ListItem-radius": (theme) => theme.vars.radius.sm,
                    }}
                >
                    <ListItem>
                        <ListItemButton selected={notice.url === location.pathname} onClick={() => handleMenuClick(notice.url)}>
                            <CampaignIcon />
                            <ListItemContent>
                                <Typography level="title-sm">{notice.title}</Typography>
                            </ListItemContent>
                        </ListItemButton>
                    </ListItem>
                    {userInfo.bizCode === "E0000" && userInfo.isAdmin ? (
                        <ListItem nested>
                            <Toggler
                                renderToggle={({ open, setOpen }) => (
                                    <ListItemButton onClick={() => setOpen(!open)}>
                                        <SettingsRoundedIcon />
                                        <ListItemContent>
                                            <Typography level="title-sm">{admin.title}</Typography>
                                        </ListItemContent>
                                        <KeyboardArrowDownIcon sx={{ transform: open ? "rotate(180deg)" : "none" }} />
                                    </ListItemButton>
                                )}
                            >
                                <List sx={{ gap: 0.5, mt: 0.5 }}>
                                    {admin.children.map((children) => {
                                        return (
                                            <ListItem key={children.id}>
                                                <ListItemButton selected={children.url === location.pathname} onClick={() => handleMenuClick(children.url)} sx={{ fontSize: "13px" }}>
                                                    {children.title}
                                                </ListItemButton>
                                            </ListItem>
                                        );
                                    })}
                                </List>
                            </Toggler>
                        </ListItem>
                    ) : (
                        ""
                    )}
                    <ListItem nested>
                        <Toggler
                            renderToggle={({ open, setOpen }) => (
                                <ListItemButton onClick={() => setOpen(!open)}>
                                    <AddchartRoundedIcon />
                                    <ListItemContent>
                                        <Typography level="title-sm">{vendor.title}</Typography>
                                    </ListItemContent>
                                    <KeyboardArrowDownIcon sx={{ transform: open ? "rotate(180deg)" : "none" }} />
                                </ListItemButton>
                            )}
                        >
                            <List sx={{ gap: 0.5, mt: 0.5 }}>
                                {vendor.children.map((children) => {
                                    return (
                                        <ListItem key={children.id}>
                                            <ListItemButton selected={children.url === location.pathname} onClick={() => handleMenuClick(children.url)} sx={{ fontSize: "13px" }}>
                                                {children.title}
                                            </ListItemButton>
                                        </ListItem>
                                    );
                                })}
                            </List>
                        </Toggler>
                    </ListItem>
                    <ListItem nested>
                        <Toggler
                            renderToggle={({ open, setOpen }) => (
                                <ListItemButton onClick={() => setOpen(!open)}>
                                    <BusinessCenter />
                                    <ListItemContent>
                                        <Typography level="title-sm">{management.title}</Typography>
                                    </ListItemContent>
                                    <KeyboardArrowDownIcon sx={{ transform: open ? "rotate(180deg)" : "none" }} />
                                </ListItemButton>
                            )}
                        >
                            <List sx={{ gap: 0.5, mt: 0.5 }}>
                                {management.children.map((children) => {
                                    return (
                                        <ListItem key={children.id}>
                                            <ListItemButton selected={children.url === location.pathname} onClick={() => handleMenuClick(children.url)} sx={{ fontSize: "13px" }}>
                                                {children.title}
                                            </ListItemButton>
                                        </ListItem>
                                    );
                                })}
                            </List>
                        </Toggler>
                    </ListItem>
                </List>
            </Box>
            <Box sx={{ mt: 0, pb: 0 }}>
                <Typography level="body-xs" sx={{ textAlign: "right", color: "grey" }}>
                    Version: {packageJson.version}
                </Typography>
            </Box>
            <Divider />
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <Avatar variant="outlined" size="sm">
                    <AccountCircleRoundedIcon />
                </Avatar>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography fontWeight={700} sx={{ overflow: "hidden", textOverflow: "ellipsis", fontSize: "13px" }}>
                        {userInfo?.name} {userInfo?.isAdmin && "(관리자)"}
                    </Typography>
                    <Typography level="body-xs" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                        {userInfo?.email}
                    </Typography>
                </Box>
                <IconButton size="sm" variant="plain" color="neutral" onClick={handleLogout}>
                    <LogoutRoundedIcon />
                </IconButton>
            </Box>
        </Sheet>
    );
}

export default Sidebar;
